import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Play, BookOpen, Quote, Youtube, Facebook, ExternalLink } from "lucide-react";

export default function MayaManthanWisdom() {
  const navigate = useNavigate();
  const [wisdom, setWisdom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { id: "all", label: "All" },
    { id: "mind", label: "Mind Control" },
    { id: "chakra", label: "Chakra Knowledge" },
    { id: "advaita", label: "Advaita Teachings" },
    { id: "sadhana", label: "Sadhana Practices" }
  ];

  useEffect(() => {
    fetchWisdom();
  }, []);

  const fetchWisdom = async () => {
    try {
      const response = await axios.get(`${API}/maya-manthan/wisdom/daily`);
      setWisdom(response.data);
    } catch (error) {
      console.error("Error fetching wisdom:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a1a] relative" data-testid="maya-wisdom">
      <div className="fixed inset-0 bg-gradient-to-b from-[#0d0d2b] via-[#0a0a1a] to-[#050510]" />

      {/* Back Button */}
      <button
        onClick={() => navigate('/maya-manthan')}
        className="fixed top-6 left-6 z-50 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
      >
        <ArrowLeft className="w-5 h-5 text-gray-400" />
      </button>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-serif text-4xl text-white mb-4">Daily Wisdom Feed</h1>
          <p className="text-gray-400">Nourish your soul with teachings and insights</p>
        </motion.div>

        {/* Quote of the Day */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-indigo-900/40 to-purple-900/40 border border-indigo-500/20 rounded-2xl p-8 mb-8 text-center"
        >
          <Quote className="w-8 h-8 text-amber-400 mx-auto mb-4 opacity-50" />
          <p className="font-serif text-2xl text-white italic mb-4">
            "{wisdom?.quote?.text}"
          </p>
          <p className="text-amber-400">— {wisdom?.quote?.author}</p>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap gap-2 mb-8 justify-center"
        >
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                activeCategory === cat.id
                  ? 'bg-amber-500 text-black'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </motion.div>

        {/* YouTube Channel Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
              <Youtube className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-medium">Maya Manthan YouTube</h3>
              <p className="text-gray-500 text-sm">Guided meditations & teachings</p>
            </div>
          </div>
          
          <a
            href={wisdom?.youtube_channel || "https://www.youtube.com/@MayaManthan555"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-red-600/20 hover:bg-red-600/30 border border-red-600/30 rounded-xl p-4 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Play className="w-5 h-5 text-red-400" />
              <span className="text-white">Watch on YouTube</span>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-400" />
          </a>
        </motion.div>

        {/* Facebook Page Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6 mb-6"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
              <Facebook className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-medium">Maya Manthan Facebook</h3>
              <p className="text-gray-500 text-sm">Community & updates</p>
            </div>
          </div>
          
          <a
            href={wisdom?.facebook_page || "https://www.facebook.com/MayaManthan.in"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-blue-600/20 hover:bg-blue-600/30 border border-blue-600/30 rounded-xl p-4 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Facebook className="w-5 h-5 text-blue-400" />
              <span className="text-white">Follow on Facebook</span>
            </div>
            <ExternalLink className="w-4 h-4 text-gray-400" />
          </a>
        </motion.div>

        {/* Content Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-indigo-400" />
              </div>
              <h3 className="text-white font-medium">Mind Control</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">Learn techniques to master your thoughts and achieve mental clarity.</p>
            <span className="text-xs bg-white/5 text-gray-500 px-2 py-1 rounded-full">Beginner</span>
          </div>

          <div className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <span className="text-amber-400">🕉️</span>
              </div>
              <h3 className="text-white font-medium">Chakra Knowledge</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">Understand your energy centers and how to balance them.</p>
            <span className="text-xs bg-white/5 text-gray-500 px-2 py-1 rounded-full">Intermediate</span>
          </div>

          <div className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <span className="text-purple-400">✨</span>
              </div>
              <h3 className="text-white font-medium">Advaita Teachings</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">Non-dual wisdom for realizing your true nature.</p>
            <span className="text-xs bg-white/5 text-gray-500 px-2 py-1 rounded-full">Advanced</span>
          </div>

          <div className="bg-[#0d0d2b]/60 backdrop-blur-xl border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <span className="text-green-400">🧘</span>
              </div>
              <h3 className="text-white font-medium">Sadhana Practices</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">Guided practices for daily spiritual discipline.</p>
            <span className="text-xs bg-white/5 text-gray-500 px-2 py-1 rounded-full">All Levels</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
